import Backbone from 'backbone'
import _ from 'lodash'
import * as d3 from 'd3-selection'
import template from './data-preview-panel.hbs'
import formatValue from 'scoreboard/components/format-value'
import Tooltip from 'scoreboard/components/tooltip'
import loadingDots from 'scoreboard/components/loading-dots.hbs'
import transformedCell from 'scoreboard/components/transformed-cell.hbs'
import i18n from 'scoreboard/components/i18n'

const DataPreviewPanel = Backbone.View.extend({

	className: 'panel panel-with-table',

	/**
	 * A panel containing a table showing a quick look at a bunch of data
	 *
	 * @constructs
	 *
	 * @param {Object} options
	 *
	 * @param {String} options.label a label for the panel
	 *
	 * @param {Object} options.preview the data to show in the preview
	 *
	 * @param {String} [options.dateFormat=momentHelper.dateFormat] a format
	 * to use when displaying dates
	 *
	 * @param {String[]} [options.columns] if specified, we'll only render
	 * these columns
	 *
	 * @param {Object} [options.preview.transformedRows] transformed rows
	 *
	 * @param {Object} [options.tooltipClasses='tooltip has-error'] classes for
	 * error tooltips
	 *
	 * @param {Boolean} [options.formatValues=true] if false, display unformatted values
	 *
	 * @param {Map<String, String>} [options.dataTypesByColumnLabel] dataTypes
	 * keyed by column label
	 *
	 * @param {Boolean} [options.formatTransformedValues=true] whether to format
	 * any transformed values
	 *
	 * @param {Map<String, Object>} [options.formattingOptionsByFieldRawColumn]
	 * formatting options for basic fields, keyed by the field's raw column name
	 * */
	initialize: function(options = {}) {
		this.options = options || {}
		this.preview = this.options.preview || {}
		this.label = this.options.label
		this.dateFormat = this.options.dateFormat
		this.formatValues = _.get(this.options, 'formatValues', true)
		this.dataTypesByColumnLabel = _.get(this.options, 'dataTypesByColumnLabel', {})
		this.formatTransformedValues = _.get(this.options, 'formatTransformedValues', true)
		this.formattingOptionsByFieldRawColumn = this.options?.formattingOptionsByFieldRawColumn || {}
	},

	render: function (options) {
		_.extend(this.options, options)
		let data = _.get(this.options, 'preview', {})
		const columns = data.columns || []
		let rows = data.rows || []
		// #29314 display transformed rows if included in preview
		let transformedRows = data.transformedRows
		if (transformedRows) {
			rows = transformedRows || []
		}
		let isEmpty = _.isEmpty(columns) && _.isEmpty(rows)
		let rowIgnored = i18n.get('admin.import.transform.examples.rowNotImported')
		const rowRemoved = i18n.get('admin.import.transform.examples.rowRemoved')

		this.$el.empty().append(template({
			label: this.label,
			hasData: !isEmpty
		}))

		if (isEmpty) {
			return this
		}

		let table = d3.select(this.el)
		var headerColumn = table.select('thead tr').selectAll('th').data(columns)
		var headerColumnEnter = headerColumn.enter().append('th')
		var headerColumns = headerColumnEnter.merge(headerColumn)

		headerColumns
			.classed('is-ignored', function (col) { return col.ignored })
			.text(col => {
				const {label, dataType} = col
				// #55454 don't overescape header labels by using _.escape here
				let value = label
				if (this.formatValues && label && dataType) {
					value = formatValue({value, dataType})
				}
				return value
			})

		// Fill in any gaps in data so <td> are rendered correctly in table
		rows.forEach(r => {
			const dataEnd = r.cells.length
			r.cells.length = columns.length
			r.cells.fill({}, dataEnd)
		})

		var dataRow = table.select('tbody').selectAll('tr').data(rows)

		var allRows = dataRow.enter().append('tr')
			.merge(dataRow)

		let dataColumn = allRows.selectAll('td').data(function (row) {
			return row.cells
		})

		dataColumn.enter()
			.append('td')
			.merge(dataColumn)
			.html((cell,i) => {
				let {value, dataType, originalValue, rawValue} = cell
				value = _.escape(value)
				// #44822 removed unneeded moveDecimalForPercent - this no
				// longer needs to be set to false for transformed values with a
				// dataType of 'percent', and defaults to true otherwise
				// #44822 if the original value was blank, this cell
				// won't have a dataType associated with it, so try to
				// determine a dataType based on the dataTypesByColumnLabelMap,
				// if one was provided
				let col = data.columns[i]
				let label = _.get(col, 'label')
				if (!_.isEmpty(this.dataTypesByColumnLabel) && _.isNil(dataType)) {
					let columnDataType = this.dataTypesByColumnLabel.get(label)
					if (!_.isNil(columnDataType)) {
						dataType =  columnDataType
					}
				}
				// #53703 see if there is a more specific dataType to
				// use when displaying any transformed values
				// For example, we may have a dataType of 'number'
				// from this.dataTypesByColumnLabel (from dataSourceMetadata)
				// but a dataType of 'currency' from
				// this.formattingOptionsByFieldRawColumn.
				// This will allow the correct display of precision for currency
				// when displaying the transformed value
				let dataTypeForTransformedValues = dataType
				let precisionForTransformedValues
				if (!_.isEmpty(this.formattingOptionsByFieldRawColumn)) {
					const formattingOptions = this.formattingOptionsByFieldRawColumn.get(label)
					if (!_.isNil(formattingOptions)) {
						const {precision, dataType} = formattingOptions
						if (!_.isUndefined(dataType)) {
							dataTypeForTransformedValues = dataType
						}
						if (!_.isUndefined(precision)) {
							precisionForTransformedValues = precision
						}
					}
				}

				if (!_.isNil(value) && dataType) {
					if (this.formatValues) {
						value = formatValue({
							value, dataType,
							dateFormat: this.dateFormat,
							shorten: false
						})
					}
				}
				// #29314 #30529 if cell reflects transformed data, display
				// original and transformed value
				// #36180 since the originalValue may have been transformed more
				// than once with multiple thens, let's display the rawValue as
				// the "original value"
				if (!_.isNil(originalValue) && _.isNil(cell.error)) {
					value = value || ''
					// #53703 we will want to format the transformed values
					// unless otherwise specified. Currently, both value imports
					// and datasets should format the transformed values.
					// Note that 'this.formatValues' is false for datasets
					// because the original values should display unformatted
					// (see #35170)
					if (this.formatTransformedValues) {
						// we need to format the transformed value appropriately
						// in case there was a date transformation #50190
						value = formatValue({
							value,
							dataType: dataTypeForTransformedValues,
							precision: precisionForTransformedValues,
							dateFormat: this.dateFormat,
							shorten: false
						})
					}
					originalValue = rawValue || ''
					// #32744 try to format the originalValue if we have a dataType
					// #36180 showing the raw value instead
					rawValue = rawValue || ''
					rawValue = _.escape(rawValue)
					if (this.formatValues && dataType) {
						originalValue = formatValue({
							value: rawValue,
							dataType,
							dateFormat: this.dateFormat,
							shorten: false
						})
					}
					return transformedCell({value, originalValue})
				}
				return value
			})
			.classed('has-preview-error', function (cell) { return cell.error })
			.classed('is-ignored', function (cell, i) {
				return data.columns[i].ignored
			})
			.classed('is-ignored', (cell) => {
				return cell.error && (cell.error === rowIgnored || cell.error === rowRemoved)
			})
			.on('mouseenter', (e, cell) => this._showErrorTooltip(e, cell))
			.on('mouseleave', this._removeErrorTooltip.bind(this))

		return this
	},

	/**
	 * Replaces the data table with loading dots
	 */
	loading: function() {
		this.$('table').replaceWith(`
			<div class="min-height-vertical-center">
				<div class="loading-dots-container">
					${loadingDots()}
				</div>
			</div>
		`)
	},

	setDateFormat: function(dateFormat) {
		this.dateFormat = dateFormat
	},

	_showErrorTooltip: function (e, cellData) {
		if (!cellData.error) {
			return
		}

		let tooltipClasses = _.get(this.options, 'tooltipClasses', 'tooltip has-error')
		let attachTo = this.$(e.target)

		this._tooltip = new Tooltip({
			attachTo,
			className: tooltipClasses,
			placement: 'above',
			arrow: 'center',
			content: _.escape(cellData.error),
			closeOnAttachedToClick: false
		}).render()
	},

	_removeErrorTooltip: function () {
		this._tooltip && this._tooltip.remove()
		delete this._tooltip
	},

	remove: function() {
		Backbone.View.prototype.remove.apply(this, arguments)
		this._removeErrorTooltip()
	}

})

export default DataPreviewPanel

DataPreviewPanel.__filename__ = 'components/data-preview-panel.js'
